import { supabase } from "./supabase";

export const affiliateWiseAccountService = {
  /**
   * Get all Wise recipient IDs for an affiliate
   */
  async getWiseRecipientIds(affiliateName) {
    try {
      const { data, error } = await supabase
        .from('affiliate_wise_accounts')
        .select('wise_recipient_id')
        .eq('affiliate_name', affiliateName);

      if (error) {
        console.error('❌ Error fetching wise recipient IDs:', error);
        return { success: false, error: error.message };
      }

      const recipientIds = data.map(row => row.wise_recipient_id);
      return { success: true, data: recipientIds };
    } catch (err) {
      console.error('❌ Error in getWiseRecipientIds:', err);
      return { success: false, error: err.message };
    }
  },

  /**
   * Save a new Wise recipient ID for an affiliate (prevents duplicates)
   */
  async saveWiseRecipientId(affiliateName, wiseRecipientId) {
    try {
      // First check if this recipient ID exists for ANY affiliate
      const { data: existingRecords, error: checkError } = await supabase
        .from('affiliate_wise_accounts')
        .select('affiliate_name')
        .eq('wise_recipient_id', wiseRecipientId.toString());

      if (checkError) {
        console.error('❌ Error checking for existing recipient ID:', checkError);
        return { success: false, error: checkError.message };
      }

      if (existingRecords && existingRecords.length > 0) {
        const existingAffiliate = existingRecords[0].affiliate_name;
        
        if (existingAffiliate === affiliateName) {
          console.log(`❌ Wise recipient ID ${wiseRecipientId} already exists for ${affiliateName}`);
          return { 
            success: false, 
            error: `This recipient is already added to your account. Each recipient can only be added once.`,
            isDuplicate: true,
            duplicateType: 'same_affiliate'
          };
        } else {
          console.log(`❌ Wise recipient ID ${wiseRecipientId} already exists for different affiliate: ${existingAffiliate}`);
          return { 
            success: false, 
            error: `This recipient ID is already associated with another affiliate (${existingAffiliate}). Each recipient can only be used once across the system.`,
            isDuplicate: true,
            duplicateType: 'different_affiliate'
          };
        }
      }
      
      // Insert new record (no duplicates found)
      const { data, error } = await supabase
        .from('affiliate_wise_accounts')
        .insert({
          affiliate_name: affiliateName,
          wise_recipient_id: wiseRecipientId.toString()
        })
        .select();

      if (error) {
        console.error('❌ Error saving wise recipient ID:', error);
        return { success: false, error: error.message };
      }

      if (!Array.isArray(data) || data.length === 0 || !data[0]) {
        console.error(
          '❌ Wise recipient ID insert returned no row:',
          { affiliateName, wiseRecipientId, data }
        );
        return {
          success: false,
          error:
            "Save completed without a returned row. The insert may have been blocked or not persisted.",
        };
      }

      console.log(`✅ Successfully added recipient ID ${wiseRecipientId} for ${affiliateName}`);
      return { success: true, data: data[0] };
    } catch (err) {
      console.error('❌ Error in saveWiseRecipientId:', err);
      return { success: false, error: err.message };
    }
  },

  /**
   * Remove a Wise recipient ID for an affiliate
   */
  async removeWiseRecipientId(affiliateName, wiseRecipientId) {
    try {
      const { error } = await supabase
        .from('affiliate_wise_accounts')
        .delete()
        .eq('affiliate_name', affiliateName)
        .eq('wise_recipient_id', wiseRecipientId.toString());

      if (error) {
        console.error('❌ Error removing wise recipient ID:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (err) {
      console.error('❌ Error in removeWiseRecipientId:', err);
      return { success: false, error: err.message };
    }
  },

  /**
   * Check if a Wise recipient ID exists for an affiliate
   */
  async hasWiseRecipientId(affiliateName, wiseRecipientId) {
    try {
      const { data, error } = await supabase
        .from('affiliate_wise_accounts')
        .select('id')
        .eq('affiliate_name', affiliateName)
        .eq('wise_recipient_id', wiseRecipientId.toString())
        .limit(1);

      if (error) {
        console.error('❌ Error checking wise recipient ID:', error);
        return { success: false, error: error.message };
      }

      return { success: true, exists: data.length > 0 };
    } catch (err) {
      console.error('❌ Error in hasWiseRecipientId:', err);
      return { success: false, error: err.message };
    }
  },

  /**
   * Upsert a Wise recipient ID for an affiliate (alternative method using upsert)
   */
  async upsertWiseRecipientId(affiliateName, wiseRecipientId) {
    try {
      const { data, error } = await supabase
        .from('affiliate_wise_accounts')
        .upsert({
          affiliate_name: affiliateName,
          wise_recipient_id: wiseRecipientId.toString(),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'affiliate_name,wise_recipient_id'
        })
        .select();

      if (error) {
        console.error('❌ Error upserting wise recipient ID:', error);
        return { success: false, error: error.message };
      }

      if (!Array.isArray(data) || data.length === 0 || !data[0]) {
        console.error(
          '❌ Wise recipient ID upsert returned no row:',
          { affiliateName, wiseRecipientId, data }
        );
        return {
          success: false,
          error:
            "Save completed without a returned row. The upsert may have been blocked or not persisted.",
        };
      }

      return { success: true, data: data[0] };
    } catch (err) {
      console.error('❌ Error in upsertWiseRecipientId:', err);
      return { success: false, error: err.message };
    }
  }
};
